// src/hooks/useAlgoliaChat.js — State management partagé pour les pages de recherche LLM + Algolia

import { useState, useCallback, useRef } from "react";
import { searchAlgoliaMulti, callLLM, countActiveFilters, geocodeVille } from "../api/search";
import { transcribeAudio } from "../api/transcribe";

/**
 * @param {object} config
 * @param {string}   config.algoliaIndex       - Nom de l'index Algolia
 * @param {Array}    config.filterDimensions   - [{ key, label, filtersDataKey?, values?, aliases?, labelMap? }]
 * @param {object}   config.filtersData        - Données des filtres depuis FiltersContext (ou null)
 * @param {boolean}  config.geo                - Active la géolocalisation (villes LLM → aroundLatLng Algolia)
 */
export function useAlgoliaChat({ algoliaIndex, assistantName, filterDimensions, filtersData, geo = false }) {
  const facetKeys = filterDimensions.map((d) => d.key);
  const visibleFilterKeys = filterDimensions.filter((d) => !d.hidden).map((d) => d.key);

  // ─── État ─────────────────────────────────────────────────────────────────
  const [inputValue, setInputValue] = useState("");
  const [convHistory, setConvHistory] = useState([]);
  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [summary, setSummary] = useState("");
  const [filterState, setFilterState] = useState({});
  const [nbHits, setNbHits] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedHit, setSelectedHit] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [geoLatLngs, setGeoLatLngs] = useState([]);
  const [geoRadius, setGeoRadius] = useState(30000);

  // ─── Refs ─────────────────────────────────────────────────────────────────
  const inputRef = useRef(null);
  const observerRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const autoStopTimerRef = useRef(null);
  const llmSummaryRef = useRef("");
  const llmFilterStateRef = useRef({});

  const searchCtx = useRef({
    ftsQuery: "",
    filterState: {},
    numericFilters: [],
    geoLatLngs: [],
    geoRadius: 30000,
    page: 0,
    nbPages: 0,
  });

  const activeCount = visibleFilterKeys.reduce((acc, key) => acc + (filterState[key]?.length || 0), 0);

  // ─── VOICE RECORDING ──────────────────────────────────────────────────────
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      setRecording(false);
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
        autoStopTimerRef.current = null;
      }
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(mediaStream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        mediaStream.getTracks().forEach((t) => t.stop());
        const authToken = localStorage.getItem("authToken");
        if (authToken) {
          setTranscribing(true);
          const transcript = await transcribeAudio(audioBlob, authToken);
          if (transcript) setInputValue(transcript);
          setTranscribing(false);
        }
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setRecording(true);
      autoStopTimerRef.current = setTimeout(() => stopRecording(), 60 * 1000);
    } catch { alert("Impossible d'accéder au micro."); }
  }, [stopRecording]);

  const toggleRecording = useCallback(() => {
    if (recording) stopRecording(); else startRecording();
  }, [recording, startRecording, stopRecording]);

  // ─── SEARCH ───────────────────────────────────────────────────────────────
  const doSearch = useCallback(async (ftsQ, fs, numFil, pg = 0) => {
    const { geoLatLngs: ctxGeo, geoRadius: ctxRadius } = searchCtx.current;
    const { hits, nbHits: nh, nbPages: np, facets: f } = await searchAlgoliaMulti(
      algoliaIndex, ftsQ, fs, ctxGeo, ctxRadius, numFil, facetKeys, pg, filterDimensions
    );
    if (pg === 0) { setResults(hits); setNbHits(nh); setFacets(f); }
    else { setResults((prev) => [...prev, ...hits]); setNbHits(nh); }
    searchCtx.current.page = pg;
    searchCtx.current.nbPages = np;
  }, [algoliaIndex]); // facetKeys et filterDimensions stables (config constante)

  const sentinelRef = useCallback((node) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (!node) return;
    observerRef.current = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || loadingMoreRef.current) return;
      const { page, nbPages, ftsQuery, filterState: fs, numericFilters, geoLatLngs: ctxGeo, geoRadius: ctxRadius } = searchCtx.current;
      if (page >= nbPages - 1) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
      searchAlgoliaMulti(algoliaIndex, ftsQuery, fs, ctxGeo, ctxRadius, numericFilters, facetKeys, page + 1, filterDimensions).then(({ hits, nbHits: nh, nbPages: np }) => {
        setResults((prev) => [...prev, ...hits]);
        setNbHits(nh);
        searchCtx.current.page = page + 1;
        searchCtx.current.nbPages = np;
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
    }, { rootMargin: "200px" });
    observerRef.current.observe(node);
  }, [algoliaIndex]); // facetKeys et filterDimensions stables (config constante)

  const runSearch = useCallback(async (userText, history = []) => {
    if (!userText.trim()) return;
    setThinking(true);
    const updatedHistory = [...history, { role: "user", content: userText }];
    const parsed = await callLLM(updatedHistory, assistantName);
    if (!parsed) { setThinking(false); return; }

    // Géolocalisation (si activée dans la config)
    let resolvedGeoLatLngs = [];
    let resolvedGeoRadius = 30000;
    if (geo && parsed.villes?.length > 0) {
      const coords = await Promise.all(parsed.villes.map(geocodeVille));
      resolvedGeoLatLngs = coords.filter(Boolean).map(({ lat, lng }) => `${lat},${lng}`);
      resolvedGeoRadius = parsed.aroundRadius || 30000;
    }

    const assistantMsg = { role: "assistant", content: JSON.stringify(parsed) };
    setConvHistory([...updatedHistory, assistantMsg]);

    const fs = parsed.filterState || {};
    const ftsQuery = parsed.query || "";
    const numericFilters = parsed.numericFilters || [];

    searchCtx.current = {
      ftsQuery, filterState: fs, numericFilters,
      geoLatLngs: resolvedGeoLatLngs, geoRadius: resolvedGeoRadius,
      page: 0, nbPages: 0,
    };
    setFilterState(fs);
    setGeoLatLngs(resolvedGeoLatLngs);
    setGeoRadius(resolvedGeoRadius);
    llmSummaryRef.current = (parsed.summary || "").replace(/\.+$/, "").trim();
    llmFilterStateRef.current = fs;
    setSummary(llmSummaryRef.current);
    setHasSearched(true);
    setShowFilters(false);

    await doSearch(ftsQuery, fs, numericFilters, 0);
    setThinking(false);
  }, [doSearch, filtersData, geo]);

  const syncHistoryFilters = useCallback((newFilterState) => {
    setConvHistory((history) => {
      if (history.length === 0) return history;
      const last = history[history.length - 1];
      if (last.role !== "assistant") return history;
      try {
        const parsed = JSON.parse(last.content);
        return [
          ...history.slice(0, -1),
          { role: "assistant", content: JSON.stringify({ ...parsed, filterState: newFilterState }) },
        ];
      } catch { return history; }
    });
  }, []);

  const buildSummaryWithFilters = useCallback((fs) => {
    const base = llmSummaryRef.current;
    const llmFs = llmFilterStateRef.current;
    const visibleDims = filterDimensions.filter((dim) => !dim.hidden);

    // Seuls les filtres ajoutés manuellement (pas dans le filterState initial du LLM)
    const added = visibleDims
      .flatMap((dim) => {
        const cur = fs[dim.key] || [];
        const llm = llmFs[dim.key] || [];
        return cur.filter((v) => !llm.includes(v)).map((v) => dim.labelMap?.[v] ?? v);
      })
      .filter(Boolean);

    if (!base) return added.join(", ");
    return added.length ? `${base} · ${added.join(", ")}` : base;
  }, [filterDimensions]);

  const handleFilterToggle = useCallback((key, value) => {
    const prev = searchCtx.current.filterState;
    const current = prev[key] || [];
    const updatedVals = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const newState = { ...prev, [key]: updatedVals };

    searchCtx.current.filterState = newState;
    setFilterState(newState);
    setSummary(buildSummaryWithFilters(newState));
    syncHistoryFilters(newState);

    const totalActive = Object.values(newState).reduce((acc, v) => acc + (v?.length || 0), 0);
    const { ftsQuery, numericFilters } = searchCtx.current;

    if (totalActive > 0 || llmSummaryRef.current) {
      setHasSearched(true);
      setFilterLoading(true);
      doSearch(ftsQuery, newState, numericFilters, 0).finally(() => setFilterLoading(false));
    } else {
      setHasSearched(false);
      setResults([]);
      setNbHits(0);
      setFacets({});
      setSummary("");
    }
  }, [doSearch, syncHistoryFilters, buildSummaryWithFilters]);

  const handleFilterReset = useCallback(() => {
    const empty = {};
    searchCtx.current.filterState = empty;
    setFilterState(empty);

    if (llmSummaryRef.current) {
      setSummary(llmSummaryRef.current);
      syncHistoryFilters(empty);
      setFilterLoading(true);
      const { ftsQuery, numericFilters } = searchCtx.current;
      doSearch(ftsQuery, empty, numericFilters, 0).finally(() => setFilterLoading(false));
    } else {
      setHasSearched(false);
      setResults([]);
      setNbHits(0);
      setFacets({});
      setSummary("");
    }
  }, [doSearch, syncHistoryFilters]);

  const handleSubmit = useCallback(() => {
    if (!inputValue.trim()) return;
    const text = inputValue;
    setInputValue("");
    if (!hasSearched || !llmSummaryRef.current) {
      setConvHistory([]);
      runSearch(text, []);
    } else {
      runSearch(text, convHistory);
    }
  }, [inputValue, hasSearched, convHistory, runSearch]);

  const handleReset = useCallback(() => {
    setHasSearched(false);
    setResults([]);
    setConvHistory([]);
    setSummary("");
    setFilterState({});
    setFacets({});
    setNbHits(0);
    setShowFilters(false);
    setSelectedHit(null);
    setInputValue("");
    setGeoLatLngs([]);
    setGeoRadius(30000);
    llmSummaryRef.current = "";
    llmFilterStateRef.current = {};
    searchCtx.current = { ftsQuery: "", filterState: {}, numericFilters: [], geoLatLngs: [], geoRadius: 30000, page: 0, nbPages: 0 };
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  return {
    inputValue, setInputValue,
    results,
    facets,
    hasSearched,
    thinking,
    filterLoading,
    loadingMore,
    summary,
    filterState,
    nbHits,
    showFilters, setShowFilters,
    selectedHit, setSelectedHit,
    recording,
    transcribing,
    activeCount,
    geoLatLngs,
    geoRadius,
    inputRef,
    sentinelRef,
    handleSubmit,
    handleReset,
    handleFilterToggle,
    handleFilterReset,
    toggleRecording,
    runSearch,
  };
}
